import { useShopifyAppContext, shopifyQueryKey } from '../../ShopifyAppContext'
/**
 * What: Renders recent Shopify Web Pixel activity for merchant troubleshooting.
 * Transport / data-flow: Calls the Shopify Worker `/api/web-pixel/activity` route with App Bridge auth and displays its zero-PII read model.
 *
 */
import { Badge, BlockStack, Button, Card, InlineError, InlineStack, Text } from '@shopify/polaris'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import type {
  WebPixelActivityEvent,
  WebPixelActivityResponse,
  WebPixelActivityStatus,
} from '../../../src/shared/web-pixel-activity-contract'
import { shopifyApiFetch } from '../../shopifyApiFetch'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { describeErrorForDiagnostics, resolveMerchantErrorMessage } from '../../merchantErrors'
import { reportClientDiagnostic } from '../../clientDiagnostics'
import { trackShopifyRefetch } from '../../shopifyUiTracking'
import { supportedMicroSurveyTriggers } from '../micro-surveys/microSurveyDraft'
import { ShopifyPageSkeleton } from '../ShopifyPageSkeleton'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'

type ShopifyTokenSource = Parameters<typeof shopifyApiFetch>[0]

type WebPixelActivityLogCardProps = {
  shopify: ShopifyTokenSource
}

async function readJsonError(response: Response, fallback: string) {
  const payload = (await response.json().catch((error) => {
    console.error(error)
    return {}
  })) as { error?: unknown }

  return typeof payload.error === 'string' ? payload.error : fallback
}

async function loadActivity(shopify: ShopifyTokenSource): Promise<WebPixelActivityResponse> {
  const response = await shopifyApiFetch(shopify, '/api/web-pixel/activity?limit=20')
  if (response.ok) return response.json()

  throw new Error(await readJsonError(response, `Web Pixel activity request failed (${response.status})`))
}

function eventLabel(event: WebPixelActivityEvent) {
  if (event.eventType === 'active_survey_fetch') return 'Active survey fetch'
  if (event.eventType === 'response_submit') return 'Response submit'
  if (event.eventType === 'trigger_reached') return 'Trigger reached'
  if (event.eventType === 'trigger_candidate') return 'Trigger candidate'
  if (event.eventType === 'survey_delivery') return 'Survey delivery'
  if (event.eventType === 'survey_dismissed') return 'Survey dismissed'
  if (event.eventType === 'survey_completed') return 'Survey completed'
  return 'Pixel configuration'
}

function statusBadge(status: WebPixelActivityStatus) {
  if (status === 'success')
    return { tone: 'success' as const, label: shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.success001 }
  if (status === 'no_active_survey')
    return {
      tone: 'attention' as const,
      label: shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.noActiveSurvey002,
    }
  if (status === 'disabled')
    return { tone: 'warning' as const, label: shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.disabled003 }
  if (status === 'skipped')
    return { tone: 'info' as const, label: shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.skipped004 }
  return {
    tone: 'critical' as const,
    label: shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.needsAttention005,
  }
}

function formatTimestamp(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

/** No trigger means the event applies to any trigger; a trigger with no label is hidden rather than shown raw. */
function triggerLabel(triggerType: string | null) {
  if (triggerType === null) return shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.anyTrigger013
  return supportedMicroSurveyTriggers.find((trigger) => trigger.type === triggerType)?.label ?? null
}

function ActivityRow({ event }: { event: WebPixelActivityEvent }) {
  const badge = statusBadge(event.status)
  const trigger = triggerLabel(event.triggerType)
  const details = event.message

  return (
    <BlockStack gap="100">
      <InlineStack align="space-between" blockAlign="center" gap="300">
        <InlineStack gap="200" blockAlign="center" wrap>
          <Text as="p" fontWeight="semibold">
            {eventLabel(event)}
          </Text>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </InlineStack>
        <Text as="span" tone="subdued">
          {formatTimestamp(event.occurredAt)}
        </Text>
      </InlineStack>
      <InlineStack gap="200" wrap>
        {trigger && (
          <Text as="span" tone="subdued">
            {trigger}
          </Text>
        )}
        {details && (
          <Text as="span" tone="subdued">
            {details}
          </Text>
        )}
      </InlineStack>
    </BlockStack>
  )
}

function ActivityContent({ events }: { events: WebPixelActivityEvent[] }) {
  if (!events.length) {
    return (
      <BlockStack gap="200">
        <Text as="h2" variant="headingMd">
          {shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.noWebPixelActivityYet006}
        </Text>
        <Text as="p" tone="subdued">
          {shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.openTheStorefrontSoThe007}
        </Text>
      </BlockStack>
    )
  }

  return (
    <BlockStack gap="400">
      {events.map((event) => (
        <ActivityRow key={event.id} event={event} />
      ))}
    </BlockStack>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.unableToLoadWebPixel009}
        </Text>
        <InlineError
          message={message}
          fieldID={shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.webPixelActivity010}
        />
      </BlockStack>
    </Card>
  )
}

/** Shows recent storefront pixel activity without exposing customer or raw storefront data. */
export function WebPixelActivityLogCard({ shopify }: WebPixelActivityLogCardProps) {
  const { shopDomain, orgId, connectionId } = useShopifyAppContext()
  const activityQuery = useQuery({
    queryKey: shopifyQueryKey(shopDomain, orgId, connectionId, ['web-pixel-activity']),
    queryFn: () => loadActivity(shopify),
    enabled: !!shopDomain && !!orgId && !!connectionId,
    ...shopifyQueryPolicies.pixelActivity,
  })

  useEffect(() => {
    if (activityQuery.error) {
      console.error('Web Pixel activity load failed', describeErrorForDiagnostics(activityQuery.error))
      reportClientDiagnostic(shopify, { schema: 'shopify-web-pixel-activity', paths: ['activity_load_failed'] })
    }
  }, [activityQuery.error])

  if (activityQuery.isLoading && !activityQuery.data) {
    return (
      <ShopifyPageSkeleton
        layout="index"
        accessibilityLabel={shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.loadingWebPixelActivity008}
      />
    )
  }

  const errorMessage = activityQuery.error
    ? resolveMerchantErrorMessage(activityQuery.error, shopifyComponentCopy.shopifyApiErrors.couldNotLoad004)
    : null
  if (activityQuery.error && !activityQuery.data) {
    return <ErrorState message={errorMessage ?? shopifyComponentCopy.shopifyApiErrors.couldNotLoad004} />
  }

  return (
    <BlockStack gap="300">
      {errorMessage && <ErrorState message={errorMessage} />}
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center" gap="300">
            <Text as="h2" variant="headingMd">
              {shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.webPixelActivity011}
            </Text>
            <Button
              onClick={() => {
                void trackShopifyRefetch(shopify, () => activityQuery.refetch())
              }}
            >
              {shopifyComponentCopy.webPixelActivityWebPixelActivityLogCard.refresh012}
            </Button>
          </InlineStack>
          <ActivityContent events={activityQuery.data?.events ?? []} />
        </BlockStack>
      </Card>
    </BlockStack>
  )
}
